import { useMutation } from '@apollo/client';
import { Button, Col, Form, Input, Modal, Radio, Row, Select } from 'antd';
import { City, State } from 'country-state-city';
import { map, upperCase } from 'lodash';
import React, { useEffect, useState } from 'react';
import { PROOF_TYPE, REGEX } from '../../common/constants';
import { formValidatorRules } from '../../common/utils';
import { CREATE_CUSTOMER, UPDATE_CUSTOMER } from './graphql/Mutations';

const { email } = formValidatorRules;

function AddEditCustomerModal({
  isModalOpen,
  setIsModalOpen,
  refetch,
  selectedRecord,
  setSelectedRecord,
}) {
  const IndianState = State.getStatesOfCountry('IN');
  const [form] = Form.useForm();
  // eslint-disable-next-line no-unused-vars
  const [cityList, setCityList] = useState([]);
  const state = Form.useWatch('state', form);
  const proofType = Form.useWatch('proofType', form);

  const [userMutation, { loading }] = useMutation(
    selectedRecord ? UPDATE_CUSTOMER : CREATE_CUSTOMER,
    {
      onCompleted() {
        setSelectedRecord();
        setIsModalOpen(false);
        refetch();
      },
      onError: () => { },
    },
  );

  useEffect(() => {
    if (state) {
      setCityList([...City.getCitiesOfState('IN', state?.key)]);
    }
  }, [state]);

  const onFinish = async (values) => {
    userMutation({
      variables: {
        input: {
          ...values,
          state: values?.state?.value || values?.state,
          country: 'India',
          countryCode: '+91',
        },
        ...(selectedRecord && {
          where: { id: selectedRecord?.id },
        }),
      },
    });
  };

  return (
    <div id="add-edit-customer-modal">
      <Modal
        centered
        title={
          <h3 className="m-0 mb-10">
            {upperCase(`${selectedRecord ? 'Edit' : 'Add New'} Customer`)}
          </h3>
        }
        footer={null}
        onCancel={() => {
          setSelectedRecord();
          setIsModalOpen(false);
        }}
        open={isModalOpen}
        width={576}
        // eslint-disable-next-line no-undef
        getContainer={() => document.getElementById('add-edit-customer-modal')}
      >
        <Form
          name="basic"
          initialValues={
            selectedRecord
              ? { ...selectedRecord }
              : { proofType: PROOF_TYPE.PAN, countryCode: '+91' }
          }
          className="category-forms mt-16"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          form={form}
        >
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                label="Name"
                name="name"
                rules={[
                  {
                    whitespace: true,
                    required: true,
                    message: 'Please input name!',
                  },
                ]}
              >
                <Input placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Custom Code"
                name="customCode"
                rules={[
                  {
                    whitespace: true,
                    required: true,
                    message: 'Please input code!',
                  },
                ]}
              >
                <Input placeholder="Enter custom code" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  email,
                ]}
              >
                <Input placeholder="Enter email address" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Contact Details"
                name="phoneNo"
                rules={[
                  {
                    required: true,
                    message: 'Please input phone number!',
                  },
                  {
                    pattern: REGEX.PHONE,
                    message: 'Enter Valid Phone Number',
                  },
                ]}
              >
                <Input placeholder="Enter Phone Number" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                label="State"
                name="state"
                rules={[
                  {
                    required: true,
                    message: 'Please select state!',
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select State"
                  optionFilterProp="children"
                  labelInValue
                // onChange={() => {
                //   setCityList([]);
                //   form.setFieldsValue({ city: null });
                // }}
                >
                  {map(IndianState, (record) => (
                    <Select.Option key={record?.isoCode} value={record?.name}>
                      {record?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="City"
                name="city"
                rules={[
                  {
                    required: true,
                    message: 'Please Enter city!',
                  },
                ]}
              >
                {/* <Select
                  showSearch
                  placeholder="Select city"
                  optionFilterProp="children"
                  disabled={!cityList?.length > 0}
                >
                  {map(cityList, (record, index) => (
                    <Select.Option key={index} value={record?.name}>
                      {record?.name}
                    </Select.Option>
                  ))}
                </Select> */}
                <Input placeholder="Enter City" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item
                label="Zip Code"
                name="zipCode"
                rules={[
                  {
                    required: true,
                    message: 'Please input zip code',
                  },
                  { pattern: REGEX.ZIPCODE, message: 'Enter Valid Zip Code' },
                ]}
              >
                <Input placeholder="Enter zip code" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Country Code"
                name="countryCode"
                rules={[
                  {
                    required: true,
                    message: 'Please input country code',
                  },
                ]}
              >
                <Input disabled placeholder="Enter country code" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Proof Type"
            name="proofType"
            rules={[
              {
                required: true,
                message: 'Required!',
              },
            ]}
          >
            <Radio.Group>
              <Radio value={PROOF_TYPE.PAN}>{PROOF_TYPE.PAN}</Radio>
              <Radio value={PROOF_TYPE.GST}>{PROOF_TYPE.GST}</Radio>
              <Radio value={PROOF_TYPE.AADHAAR}>{PROOF_TYPE.AADHAAR}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Proof Number"
            name="proofNo"
            rules={[
              {
                required: true,
                message: 'Please input proof number',
              },
              {
                pattern: REGEX[proofType],
                message: 'Please enter valid proof number',
              },
            ]}
            dependencies={['proofType']}
          >
            <Input placeholder="Enter proof number" />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="full-width font-600 submit-btn"
            disabled={loading}
            loading={loading}
          >
            {selectedRecord ? `Edit` : `Add`} Customer
          </Button>
        </Form>
      </Modal>
    </div>
  );
}

export default AddEditCustomerModal;
